import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { AuthService } from './services/auth.service';
import { TranslateService } from './services/translate.service';
import { ConfigService } from './services/config.service';
import { AppSettings } from './app.settings';
import {LocalStorageService, REDIRECT_POST_LOGIN, SSO_PROVIDER} from './services/local-storage.service';
import { AddressService } from './services/address.service';
import { isInternetExplorer } from './shared/utils';
import { PaymentService } from './services/payments.service';
import {BudgetService} from './services/budget.service';
import {GoogleAnalytics} from './services/google-analytics.service';
import {MobileCheckServiceService} from './services/mobile-check-service.service';
import {User} from './models/user.model';
import { ClientCustomizationService } from './services/client-customization.service';
import {DiscountCodeService} from './services/discount-code.service';
import {RefinerService} from './services/refiner.service';
import {TelemetryService} from './services/telemetry.service';

@Injectable()
export class AppInit {

    protected appSettings: AppSettings;
    protected authService: AuthService;
    protected configService: ConfigService;
    protected translateService: TranslateService;
    protected localStorageService: LocalStorageService;
    private addressService: AddressService;
    private paymentService: PaymentService;
    private budgetService: BudgetService;
    private googleService: GoogleAnalytics;
    private mobileCheckService: MobileCheckServiceService;
    private clientCustomizationService: ClientCustomizationService;
    private discountService: DiscountCodeService;
    private refinerService: RefinerService;

    constructor(private injector: Injector) {
    }

    init(): Promise<unknown> {
        this.appSettings = this.injector.get(AppSettings);
        this.authService = this.injector.get(AuthService);
        this.configService = this.injector.get(ConfigService);
        this.translateService = this.injector.get(TranslateService);
        this.localStorageService = this.injector.get(LocalStorageService);
        this.addressService = this.injector.get(AddressService);
        this.paymentService = this.injector.get(PaymentService);
        this.budgetService = this.injector.get(BudgetService);
        this.googleService = this.injector.get(GoogleAnalytics);
        this.mobileCheckService = this.injector.get(MobileCheckServiceService);
        this.clientCustomizationService = this.injector.get(ClientCustomizationService);
        this.discountService = this.injector.get(DiscountCodeService);
        this.refinerService = this.injector.get(RefinerService);

        // When we send the index from the server, we include client-level settings
        // that may be needed before webservices can be called

        // const appConfig = document.getElementById('app-config');
        // if (appConfig) {
        //     each(JSON.parse(appConfig.innerText), (val, key) => {
        //         if (this.appSettings.settings.hasOwnProperty(key)) {
        //             this.appSettings.settings[key] = (val === '') ? null : val;
        //         }
        //     });
        // }

        const router = this.injector.get(Router);

        // const token = document.cookie.match('(^|;)\\s*sso_user_token\\s*=\\s*([^;]+)');
        //
        // if (token) {
        //     this.localStorageService.setItem(U_TOKEN, token.pop());
        //     this.localStorageService.setItem(U_SSO, 'true');
        // } else {
        //     console.log('sso token is not found');
        // }

        this.mobileCheckService.check();
        // AB#85946: started here so every path (login/error included) gets telemetry; the main
        // path below awaits it so the earliest requests land in the startup waterfall. init()
        // always resolves and self-bounds to TelemetryService.BOOT_BUDGET_MS: it cannot delay
        // boot past that budget nor fail it.
        const telemetryReady = this.injector.get(TelemetryService).init();

        // Capture SSO provider from redirect URL and persist in localStorage
        const urlParams = new URLSearchParams(window.location.search);
        const ssoProvider = urlParams.get('sso_provider');
        if (ssoProvider) {
            this.localStorageService.setItem(SSO_PROVIDER, ssoProvider);
            urlParams.delete('sso_provider');
            const cleanUrl = urlParams.toString()
                ? `${window.location.pathname}?${urlParams.toString()}`
                : window.location.pathname;
            window.history.replaceState({}, '', cleanUrl);
        }

        // eslint-disable-next-line no-async-promise-executor -- every await is inside try/catch that calls reject
        return new Promise(async (resolve, reject) => {
            try {
                if (window.location.pathname.indexOf('/error') === 0) {
                    resolve(true);
                    return;
                }

                if (window.location.pathname.includes('/login')) {
                    resolve(true);
                    return;
                }

                if (isInternetExplorer()) {
                    router.navigate(['/not-supported']);
                    resolve(true);
                    return;
                }

                // Instrumentation must be attached before the first requests fire or they escape the
                // RUM waterfall (AB#85946); bounded fail-open, see TelemetryService.init().
                await telemetryReady;

                // get user
                let user: User;
                try {
                    user = await firstValueFrom(this.authService.validate());

                    if (!user) {
                        console.error('user validation is failed');
                        this.localStorageService.setItem(REDIRECT_POST_LOGIN, window.location.pathname);
                        reject(new UserValidationError());
                        return;
                    }
                } catch {
                    console.error('user validation error');
                    this.localStorageService.setItem(REDIRECT_POST_LOGIN, window.location.pathname);
                    reject(new UserValidationError());
                    return;
                }
                this.googleService.submitUserId(user.id);

                const result = await firstValueFrom(this.configService.fetchFeatures(user.id));
                if (result !== true) {
                    console.error('Failed to fetch user features')
                    reject(false);
                    return;
                }

                this.refinerService.identifyUser({
                    id: String(user.id),
                    email: user.email,
                    name: user.fullName().trim(),
                    role: user.role_label,
                });

                // set client specific favicon
                this.clientCustomizationService.setFavicon();

                // set  title
                this.clientCustomizationService.setTitle();

                // Fetch independent application data in parallel to reduce initialisation time
                await Promise.all([
                    firstValueFrom(this.translateService.getAll()),
                    this.addressService.fetchAllShippingAddresses(),
                    firstValueFrom(this.configService.getCountries()),
                    firstValueFrom(this.discountService.fetchDiscountCodes())
                ]);

                const location = this.localStorageService.getItem(REDIRECT_POST_LOGIN);

                if (location) {
                    this.localStorageService.removeItem(REDIRECT_POST_LOGIN);
                    if (window.location.pathname === '/' && this.isValidRedirectPath(location)) {
                        router.navigate([location]);
                    }
                }
                resolve(true);
            } catch (e) {
                reject(e);
            }
        });
    }

    private isValidRedirectPath(path: string): boolean {
        // Reject anything that looks like a protocol or external URL
        if (path.includes('://') || path.startsWith('//')) {
            return false;
        }
        if (!path.startsWith('/')) {
            return false;
        }
        return true;
    }

    // init anything that is user specific that must be ready after login, before view init
    async initPostLogin(): Promise<boolean> {
        await Promise.all([
            this.addressService.fetchAllShippingAddresses(),
            firstValueFrom(this.configService.getCountries())
        ]);

        return true;
    }
}
export class UserValidationError extends Error {

    constructor() {

        super('User validation failed');

        this.name = 'UserValidationError';

    }

}
